import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  RULESETS,
  inviteLink,
  type Campaign,
  type JoinConflict,
  type JoinRequest,
  type Seat,
} from '@rollcall/contracts'
import type { TFunction } from 'i18next'
import type { Api } from '../api.js'
import type { StreamState } from '../events.js'
import { useT } from './i18n-context.js'
import { SectionHead } from './hint.js'
import { EditChanges, JoinReview } from './join-review.js'
import { contentName } from './labels.js'
import { InitiativePanel, PartyPanel, RollFeed, useMasterState } from './master.js'
import type { Heading } from './app.js'

/**
 * Кампания глазами мастера (D8, D51).
 *
 * Ссылка одна на всю кампанию и живёт до отзыва: мастер бросает её
 * в общий чат, а места заранее не заводит — предел мест стоит
 * в настройках, и место появляется в тот момент, когда мастер
 * подтверждает заявку.
 *
 * Отсюда и устройство экрана. Ссылка и предел стоят сверху, очередь
 * заявок — сразу под ними, список сидящих — ниже. Раз ссылку
 * пересылают дальше без ведома мастера, очередь заявок остаётся
 * единственной защитой, и заявка показывает не «кто-то постучался»,
 * а имя, вид, класс и уровень персонажа: подтверждение не должно быть
 * кнопкой, которую жмут не глядя.
 *
 * Экран слушает события кампании (D2): заявка появляется сама,
 * подтверждение и освобождение места — тоже. Руками обновлять
 * не нужно, и признак связи в шапке говорит, работает ли это сейчас.
 *
 * Событие не несёт нового состояния — только то, что оно изменилось.
 * Экран по событию перечитывает списки, а не собирает их из полей
 * события: два способа получить одно и то же состояние однажды
 * разойдутся, и разойдутся молча (D3).
 *
 * Панель мастера живёт здесь же, а не отдельным экраном: за столом
 * мастер смотрит на партию, на очередь боя и на очередь заявок
 * одновременно, и ходить между экранами ради этого нечего (D10).
 */

/** Отказ сервера одной строкой. Текст приходит с сервера — он объясняет. */
function Refusal({ error }: { error: unknown }) {
  const t = useT()
  if (error === null || error === undefined) return null
  const text = error instanceof Error ? error.message : t('join.failed')
  return (
    <p className="app-problems" role="alert">
      {text}
    </p>
  )
}

/**
 * Строка про заявку: кого именно пускают за стол.
 *
 * Вид, класс и уровень — то, по чему мастер отличает своего игрока
 * от человека, которому ссылку переслали (D51). Названия вида
 * и класса переводятся по ключу записи, а имя из контента остаётся
 * запасным вариантом (D11, D32).
 */
/**
 * Причина отказа словами игрока (D61).
 *
 * Собирается из того же разбора, что видит мастер: отказ без причины
 * оставляет человека гадать, что чинить, а придумывать причину
 * мастеру руками — лишняя работа ради того, что уже посчитано.
 */
function rejectNote(
  t: TFunction,
  conflicts: readonly JoinConflict[],
): string | undefined {
  if (conflicts.length === 0) return undefined
  return conflicts
    .map((conflict) =>
      conflict.kind === 'level'
        ? t('campaigns.review.level.what', {
            character: conflict.characterLevel,
            campaign: conflict.campaignLevel,
          })
        : t(
            `campaigns.review.${conflict.kind === 'missing-content' ? 'missing' : conflict.kind === 'custom-disabled' ? 'disabled' : 'unapproved'}.what`,
            {
              names: conflict.refs.map((ref) => ref.name).join(', '),
            },
          ),
    )
    .join(' ')
}

function about(t: TFunction, request: JoinRequest): string {
  const parts: string[] = []
  const character = request.character
  if (character !== null) {
    if (character.species !== null) {
      parts.push(contentName(t, character.species.id, character.species.name))
    }
    if (character.characterClass !== null) {
      const { id, name } = character.characterClass
      parts.push(contentName(t, id, name))
    }
    parts.push(t('campaigns.requests.level', { level: character.level }))
  }
  // Откуда персонаж — подсказка от клиента, а не факт (D51); у заявки
  // на смену устройства её нет вовсе, и там говорится, что это она.
  parts.push(
    request.origin === null
      ? t(`campaigns.requests.${request.kind}`)
      : t(`campaigns.requests.origin.${request.origin}`),
  )
  return parts.join(' · ')
}

/**
 * Адрес приложения для ссылки-приглашения.
 *
 * Сервер ссылку не собирает: у общей ссылки токен открытый (D51),
 * и знать, по какому адресу открыт клиент, — дело клиента. В тестах
 * `window` есть; пустой адрес оставит ссылку относительной, но не
 * уронит экран.
 */
function appOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin
}

export function CampaignListScreen({
  api,
  onOpen,
}: {
  readonly api: Api
  readonly onOpen: (campaign: Campaign) => void
}) {
  const t = useT()
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null)
  const [withArchived, setWithArchived] = useState(false)
  const [title, setTitle] = useState('')
  const [custom, setCustom] = useState(false)
  /** Предел участников (D51). Шесть — обычный стол; правится потом. */
  const [seatLimit, setSeatLimit] = useState('6')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const reload = useCallback(() => {
    void api
      .listCampaigns(withArchived)
      .then(setCampaigns)
      .catch((cause: unknown) => {
        setError(cause)
        setCampaigns([])
      })
  }, [api, withArchived])

  useEffect(reload, [reload])

  const create = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .createCampaign({
        title,
        // Значение одно (D6), и берётся оно из списка, а не из строки:
        // вторая система появится решением.
        ruleset: RULESETS[0],
        useCustomContent: custom,
        seatLimit: Number(seatLimit),
      })
      .then(() => {
        setTitle('')
        setCustom(false)
        setSeatLimit('6')
        reload()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const archive = (campaign: Campaign) => {
    void api
      .archiveCampaign(campaign.id, campaign.archivedAt === null)
      .then(reload)
      .catch((cause: unknown) => {
        setError(cause)
      })
  }

  return (
    <section className="app-screen">
      <h2>{t('campaigns.title')}</h2>

      <form className="app-form" onSubmit={create}>
        <label className="app-field">
          <span className="app-field-label">{t('campaigns.name')}</span>
          <input
            required
            value={title}
            onChange={(event) => {
              setTitle(event.target.value)
            }}
          />
        </label>
        <label className="app-field">
          <span className="app-field-label">{t('campaigns.invite.limit')}</span>
          <input
            required
            type="number"
            min={1}
            max={64}
            value={seatLimit}
            onChange={(event) => {
              setSeatLimit(event.target.value)
            }}
          />
        </label>
        <span className="app-field-hint">{t('campaigns.invite.limitHint')}</span>
        <label className="app-option">
          <input
            type="checkbox"
            checked={custom}
            onChange={(event) => {
              setCustom(event.target.checked)
            }}
          />
          <span>{t('campaigns.customContent')}</span>
        </label>
        <span className="app-field-hint">{t('campaigns.customContentHint')}</span>
        <Refusal error={error} />
        <button type="submit" className="app-button" disabled={busy}>
          {t('campaigns.create')}
        </button>
      </form>

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            setWithArchived((current) => !current)
          }}
        >
          {withArchived ? t('campaigns.hideArchived') : t('campaigns.showArchived')}
        </button>
      </div>

      {campaigns !== null && campaigns.length === 0 && <p>{t('campaigns.empty')}</p>}

      <ul className="app-list">
        {(campaigns ?? []).map((campaign) => (
          <li key={campaign.id} className="app-card">
            <h3>{campaign.title}</h3>
            <p className="app-value-note">
              {/* Предел, а не число заведённых мест: мест заранее
                  больше нет, и «мест: 0» у живой кампании читалось бы
                  как поломка (D51). */}
              {t('campaigns.summary', {
                used: campaign.seatsUsed,
                limit: campaign.seatLimit,
              })}
              {campaign.pendingRequests > 0 &&
                ` · ${t('campaigns.pending', { count: campaign.pendingRequests })}`}
              {campaign.archivedAt !== null && ` · ${t('campaigns.archived')}`}
            </p>
            <div className="app-actions">
              <button
                type="button"
                className="app-button"
                onClick={() => {
                  onOpen(campaign)
                }}
              >
                {t('campaigns.open')}
              </button>
              <button
                type="button"
                className="app-button app-button-quiet"
                onClick={() => {
                  archive(campaign)
                }}
              >
                {campaign.archivedAt === null
                  ? t('campaigns.archive')
                  : t('campaigns.unarchive')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* Кнопки удаления здесь нет и не будет: в кампании живут чужие
          персонажи, и уносить их нечем (D37). */}
      <p className="app-field-hint">{t('campaigns.noDelete')}</p>
    </section>
  )
}

/**
 * Признак связи.
 *
 * Без него человек не отличит «ничего не происходит» от «оборвалось»,
 * и это худшее из состояний интерфейса: экран выглядит рабочим
 * и врёт.
 */
function Connection({ state }: { readonly state: StreamState }) {
  const t = useT()
  return (
    <p className="app-value-note" data-testid="stream-state" data-state={state}>
      {t(`campaigns.stream.${state}`)}
    </p>
  )
}

export function CampaignScreen({
  api,
  campaignId,
  onHeading,
  onContent,
}: {
  readonly api: Api
  readonly campaignId: string
  /**
   * Имя кампании в шапку. Приходит снизу, как имя персонажа с листа:
   * только этот экран знает, какая кампания открыта, и знает это
   * после ответа сервера, а не до него.
   */
  readonly onHeading?: ((heading: Heading | null) => void) | undefined
  /** Свои записи контента (D53): вход стоит у флага, который их подключает. */
  readonly onContent: () => void
}) {
  const t = useT()
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [seats, setSeats] = useState<Seat[]>([])
  const [requests, setRequests] = useState<JoinRequest[]>([])
  // Что мастер скажет игроку при отказе (D61): по заявке, с подсказкой
  // из разбора. Пусто — причина не сказана, и это тоже видно игроку.
  const [notes, setNotes] = useState<Readonly<Record<string, string>>>({})
  /**
   * Предел мест — строкой, а не числом: поле ввода отдаёт строку,
   * и превращать её в число на каждое нажатие значило бы стирать
   * из-под пальцев пустое поле, пока человек набирает «12».
   */
  const [limit, setLimit] = useState('')
  // Стартовый уровень кампании (D60). Как и предел мест, заполняется
  // из ответа, пока человек поле не трогал.
  const [startLevel, setStartLevel] = useState('')
  const [copied, setCopied] = useState(false)
  const [released, setReleased] = useState(false)
  /**
   * Раскрыты ли настройки кампании.
   *
   * `null` — ещё не решали: экран не знает, пуст ли стол, пока
   * не пришёл ответ сервера. Как только он пришёл, пустая кампания
   * раскрывает настройки сама — её и заводят ради приглашения
   * (D51), — а обжитая оставляет их свёрнутыми. Дальше решает
   * человек, и его выбор уже не переписывается.
   */
  const [settingsOpen, setSettingsOpen] = useState<boolean | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [connection, setConnection] = useState<StreamState>('connecting')

  // Сводка партии, очередь боя и общий журнал бросков: те же данные,
  // что и у списков, и перечитываются они тем же событием.
  const master = useMasterState(api, campaignId)
  // В зависимостях — сама функция, а не объект состояния: объект
  // новый на каждый рендер, и `useEffect` на нём крутился бы вечно.
  const reloadMaster = master.reload

  const reload = useCallback(() => {
    void api
      .listCampaigns(true)
      .then((all) => {
        const found = all.find((row) => row.id === campaignId) ?? null
        setCampaign(found)
        // Поле предела заполняется из ответа только пока человек
        // его не трогал: иначе перечитывание по событию затирало бы
        // набранное на середине числа.
        setLimit((current) =>
          current === '' && found !== null ? String(found.seatLimit) : current,
        )
        setStartLevel((current) =>
          current === '' && found !== null ? String(found.startLevel) : current,
        )
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
    void api.listSeats(campaignId).then(setSeats).catch(setError)
    void api.listJoinRequests(campaignId).then(setRequests).catch(setError)
    reloadMaster()
  }, [api, campaignId, reloadMaster])

  useEffect(reload, [reload])

  useEffect(() => {
    if (onHeading === undefined) return
    onHeading(campaign === null ? null : { title: campaign.title, subtitle: null })
    return () => {
      onHeading(null)
    }
  }, [onHeading, campaign])

  /**
   * Подписка на события кампании (D2).
   *
   * Номер последнего события живёт в `ref`, а не в состоянии:
   * менять состояние на каждое событие значило бы пересоздавать
   * подписку на каждое событие.
   */
  const seen = useRef(0)

  useEffect(() => {
    const stream = api.watchCampaign(campaignId, seen.current, {
      onEvent: (event) => {
        seen.current = event.seq
        // Что именно поменялось, экран не разбирает: он перечитывает
        // списки. Разбор пришлось бы держать в согласии с сервером,
        // а перечитывание — нет.
        reload()
      },
      onResync: (lastSeq) => {
        seen.current = lastSeq
        reload()
      },
      onState: setConnection,
    })
    /**
     * Возвращение вкладки из фона.
     *
     * Телефон замораживает таймеры и рвёт соединения молча: экран
     * возвращается, поток выглядит живым, а событий по нему больше
     * не будет. Единственный способ узнать — подключиться заново,
     * и повод к этому знает экран, а не поток.
     */
    const wake = (): void => {
      if (document.visibilityState === 'visible') stream.wake()
    }
    document.addEventListener('visibilitychange', wake)

    return () => {
      document.removeEventListener('visibilitychange', wake)
      stream.stop()
    }
  }, [api, campaignId, reload])

  const guard = (work: Promise<unknown>) => {
    setError(null)
    void work.then(reload).catch((cause: unknown) => {
      setError(cause)
    })
  }

  const saveLimit = (event: FormEvent) => {
    event.preventDefault()
    const next = Number(limit)
    if (!Number.isInteger(next)) return
    guard(api.updateCampaign({ campaignId, seatLimit: next }))
  }

  /**
   * Стартовый уровень (D60): с какого уровня мастер пускает новых.
   *
   * Отдельной формой от предела мест: это разные решения, и сохранять
   * их вместе значит менять одно, когда правили другое.
   */
  const saveStartLevel = (event: FormEvent) => {
    event.preventDefault()
    const next = Number(startLevel)
    if (!Number.isInteger(next) || next < 1 || next > 20) return
    guard(api.updateCampaign({ campaignId, startLevel: next }))
  }

  const link =
    campaign?.inviteToken == null ? null : inviteLink(appOrigin(), campaign.inviteToken)

  // Пока сервер не ответил, стол «не пуст»: раскрывать настройки
  // на мгновение, чтобы тут же свернуть, хуже, чем не раскрыть.
  const settings = settingsOpen ?? (campaign !== null && campaign.seatsUsed === 0)

  const copy = (value: string) => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true)
      })
      .catch(() => {
        // Буфера нет — ссылка всё равно на экране, и её можно
        // выделить руками.
      })
  }

  const release = (seat: Seat) => {
    setError(null)
    void api
      .releaseSeat(seat.id)
      .then(() => {
        setReleased(true)
        reload()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
  }

  return (
    <section className="app-screen" aria-label={campaign?.title ?? ''}>
      {/* Имя кампании стоит в шапке (см. `onHeading`), и вторым
          заголовком его писать незачем: там оно видно всегда,
          а здесь уезжает вверх при первой прокрутке. */}
      <Connection state={connection} />
      {/* Кнопки «К списку кампаний» здесь больше нет: в меню шапки
          есть «На главную», и оттуда видны оба списка сразу (D8).
          Вторая дорога к тому же экрану — это лишняя строка на
          каждом открытии кампании. */}
      <Refusal error={error} />

      {/* Стол мастера — двумя колонками, и в каждой своя стопка.

          До этой правки экран был двумя решётками подряд: партия
          с очередью боя, под ними заявки с составом стола. Строка
          решётки тянется по самой высокой ячейке, и на пустой
          кампании слева оставалось полколонки воздуха, пока справа
          шла очередь боя. Строк больше нет: колонка — это стопка,
          и каждая набирается своим содержимым, а не чужой высотой.

          Слева то, из чего стол собирается: партия, заявки,
          настройки. Справа то, что на столе происходит: очередь боя,
          состав, броски. Партия и очередь по-прежнему стоят рядом
          сверху — на них смотрят чаще всего.

          На узком экране обе колонки — `display: contents`, и порядок
          возвращает `order`: партия, очередь, броски, заявки, состав,
          настройки. Телефон остаётся тем же, каким был. */}

      {/* Ссылка и предел мест стоят внизу колонки и свёрнуты (D51):
          их задают один раз в начале, а экран открывают каждую игру.
          Пустая кампания разворачивает их сама. */}
      <div className="app-master">
        <div className="app-master-main">
          <div className="app-table-party">
            <PartyPanel
              api={api}
              campaignId={campaignId}
              party={master.party}
              onChanged={reload}
            />
          </div>

          <section className="app-campaign-requests">
            {/* Ссылка публична, и подтверждение — единственное, что
              стоит между чатом и столом (D51). Поэтому в заявке
              видно, кого пускают, а не только что кто-то
              постучался; объяснение — за знаком вопроса. */}
            <SectionHead
              title={t('campaigns.requests.title')}
              hints={[t('campaigns.requests.hint')]}
            />
            {requests.length === 0 ? (
              <p>{t('campaigns.requests.empty')}</p>
            ) : (
              <ul className="app-list">
                {requests.map((request) => (
                  <li key={request.id} className="app-card">
                    <h4>
                      {request.character?.name ??
                        request.seatLabel ??
                        t('campaigns.requests.unknown')}
                    </h4>
                    <p className="app-value-note">{about(t, request)}</p>
                    {/* Разбор несоответствий (D61): всё, что
                        не сходится, — одним списком до решения,
                        а не отказами по одному после. */}
                    {/* Правка (D62) в той же очереди: что было,
                        что станет. */}
                    {request.changes !== null && (
                      <EditChanges changes={request.changes} />
                    )}
                    <JoinReview
                      conflicts={request.conflicts}
                      onRaiseLevel={(level) => {
                        guard(api.updateCampaign({ campaignId, startLevel: level }))
                      }}
                    />
                    {request.status === 'pending' && (
                      <label className="app-field">
                        <span>{t('campaigns.requests.note')}</span>
                        <textarea
                          data-testid="reject-reason"
                          maxLength={500}
                          value={
                            notes[request.id] ?? rejectNote(t, request.conflicts) ?? ''
                          }
                          onChange={(event) => {
                            setNotes({ ...notes, [request.id]: event.target.value })
                          }}
                        />
                      </label>
                    )}
                    {request.status === 'rejected' ? (
                      <>
                        <p className="app-value-note">
                          {t('campaigns.requests.autoRejected')}
                        </p>
                        <p className="app-field-hint">
                          {t('campaigns.requests.autoRejectedHint')}
                        </p>
                      </>
                    ) : (
                      <div className="app-actions">
                        <button
                          type="button"
                          className="app-button"
                          onClick={() => {
                            guard(api.resolveJoinRequest(request.id, true))
                          }}
                        >
                          {request.kind === 'edit'
                            ? t('campaigns.requests.approveEdit')
                            : t('campaigns.requests.approve')}
                        </button>
                        <button
                          type="button"
                          className="app-button app-button-quiet"
                          onClick={() => {
                            // Отказ объясняется игроку: без причины
                            // он не знает, что чинить (D61). Если
                            // несоответствий нет — причины тоже нет,
                            // и поле остаётся пустым.
                            const said = (
                              notes[request.id] ??
                              rejectNote(t, request.conflicts) ??
                              ''
                            ).trim()
                            guard(
                              api.resolveJoinRequest(
                                request.id,
                                false,
                                said === '' ? undefined : said,
                              ),
                            )
                          }}
                        >
                          {t('campaigns.requests.reject')}
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="app-campaign-invite">
            {/* Настройки кампании свёрнуты: ссылку и предел мест
                задают один раз в начале, а экран открывают каждую
                игру. Развёрнуты они ровно тогда, когда за столом
                ещё никого, — пустую кампанию и заводят ради того,
                чтобы позвать людей (D51). */}
            <div className="app-section-head">
              <div className="app-section-title">
                <h3>{t('campaigns.invite.title')}</h3>
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  aria-expanded={settings}
                  aria-controls="campaign-settings"
                  data-testid="campaign-settings-toggle"
                  onClick={() => {
                    setSettingsOpen(!settings)
                  }}
                >
                  {settings ? t('campaigns.invite.hide') : t('campaigns.invite.show')}
                </button>
              </div>
            </div>

            {settings && (
              <div id="campaign-settings">
                <form className="app-form" onSubmit={saveLimit}>
                  <label className="app-field">
                    <span className="app-field-label">
                      {t('campaigns.invite.limit')}
                    </span>
                    <input
                      required
                      type="number"
                      min={1}
                      max={64}
                      value={limit}
                      onChange={(event) => {
                        setLimit(event.target.value)
                      }}
                    />
                  </label>
                  {campaign !== null && (
                    <p className="app-value-note">
                      {t('campaigns.invite.used', {
                        used: campaign.seatsUsed,
                        limit: campaign.seatLimit,
                      })}
                    </p>
                  )}
                  <button type="submit" className="app-button app-button-quiet">
                    {t('campaigns.invite.limitSave')}
                  </button>
                </form>

                {/* Стартовый уровень (D60): кампания может начинаться
                    не с первого. Персонаж при этом всё равно собирается
                    по шагам — создание плюс повышения, — и это поле
                    задаёт цель, а не способ сборки. */}
                <form className="app-form" onSubmit={saveStartLevel}>
                  <label className="app-field">
                    <span className="app-field-label">
                      {t('campaigns.invite.startLevel')}
                    </span>
                    <input
                      required
                      type="number"
                      min={1}
                      max={20}
                      value={startLevel}
                      data-testid="campaign-start-level"
                      onChange={(event) => {
                        setStartLevel(event.target.value)
                      }}
                    />
                  </label>
                  <p className="app-value-note">
                    {t('campaigns.invite.startLevelHint')}
                  </p>
                  <button type="submit" className="app-button app-button-quiet">
                    {t('campaigns.invite.startLevelSave')}
                  </button>
                </form>

                {/* Свой контент подключается к столу здесь (D30, D53):
                    флаг заводился при создании кампании и до этой правки
                    больше нигде не показывался — то есть выключить его
                    было нельзя. */}
                {campaign !== null && (
                  <>
                    <label className="app-check">
                      <input
                        type="checkbox"
                        checked={campaign.useCustomContent}
                        onChange={(event) => {
                          guard(
                            api.updateCampaign({
                              campaignId,
                              useCustomContent: event.target.checked,
                            }),
                          )
                        }}
                      />
                      <span>{t('campaigns.customContent')}</span>
                    </label>
                    {/* Вход в редактор стоит у флага, который подключает
                        записи к столу (D53): вопрос «а где их заводить?»
                        возникает ровно здесь, а не на главной. */}
                    <div className="app-actions">
                      <button
                        type="button"
                        className="app-button app-button-quiet"
                        data-testid="campaign-content"
                        onClick={onContent}
                      >
                        {t('campaigns.customContentOpen')}
                      </button>
                    </div>
                  </>
                )}

                {link === null ? (
                  <div className="app-actions">
                    <button
                      type="button"
                      className="app-button"
                      onClick={() => {
                        setCopied(false)
                        guard(api.campaignInvite(campaignId))
                      }}
                    >
                      {t('campaigns.invite.create')}
                    </button>
                  </div>
                ) : (
                  <div className="app-invite">
                    {/* Ссылку показываем целиком и сколько угодно раз: она
                лежит в чате, и мастер копирует её каждому опоздавшему
                (D51). Прятать нечего — хеша у неё нет. */}
                    <code
                      className="app-invite-link"
                      data-testid="campaign-invite-link"
                    >
                      {link}
                    </code>
                    <div className="app-actions">
                      <button
                        type="button"
                        className="app-button"
                        onClick={() => {
                          copy(link)
                        }}
                      >
                        {copied
                          ? t('campaigns.invite.copied')
                          : t('campaigns.invite.copy')}
                      </button>
                      <button
                        type="button"
                        className="app-button app-button-quiet"
                        onClick={() => {
                          setCopied(false)
                          guard(api.revokeCampaignInvite(campaignId))
                        }}
                      >
                        {t('campaigns.invite.revoke')}
                      </button>
                    </div>
                  </div>
                )}
                <SectionHead
                  title={t('campaigns.invite.about')}
                  hints={[t('campaigns.invite.hint'), t('campaigns.invite.limitHint')]}
                />
              </div>
            )}
          </section>
        </div>

        <div className="app-master-side">
          <div className="app-table-combat">
            <InitiativePanel
              api={api}
              campaignId={campaignId}
              initiative={master.initiative}
              master
              onChanged={reload}
            />
          </div>

          <section className="app-campaign-seats">
            <SectionHead
              title={t('campaigns.seats.title')}
              hints={[
                t('campaigns.seats.noCharacterHint'),
                t('campaigns.seats.releaseHint'),
              ]}
            />
            {seats.length === 0 && <p>{t('campaigns.seats.emptyNow')}</p>}
            {released && (
              <p className="app-auth-notice">{t('campaigns.seats.released')}</p>
            )}

            <ul className="app-list">
              {seats.map((seat) => (
                <li key={seat.id} className="app-card">
                  <h4>{seat.character?.name ?? seat.label}</h4>
                  <p className="app-value-note">
                    {t(`campaigns.seats.${seat.status}`)}
                    {seat.character !== null && ` · ${seat.character.code}`}
                  </p>
                  {seat.status !== 'free' && (
                    <div className="app-actions">
                      <button
                        type="button"
                        className="app-button app-button-quiet"
                        title={t('campaigns.seats.releaseHint')}
                        onClick={() => {
                          release(seat)
                        }}
                      >
                        {t('campaigns.seats.release')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <div className="app-table-rolls">
            <RollFeed rolls={master.rolls} />
          </div>
        </div>
      </div>
    </section>
  )
}
